// pages/ProductDetailPage.js - Updated with Proper Product Card Generator
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import { 
  ShoppingCart, Truck, RotateCcw, Shield, 
  ArrowLeft, Zap, Check, Heart, Share2,
  Minus, Plus, Package, X, AlertCircle, CheckCircle, Info,
  ShoppingBag, Sparkles, ChevronLeft, ChevronRight, Maximize2,
  MessageCircle, Copy, Send, Share, Download, Image as ImageIcon,
  Camera, Gift, Flower2
} from 'lucide-react';

// Custom Auto-close Centered Dialog Component
const CustomDialog = ({ isOpen, onClose, message, type, productName }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch(type) {
      case 'cart': return <ShoppingBag size={48} color="#dc2626" />;
      case 'wishlist': return <Heart size={48} color="#dc2626" fill="#dc2626" />;
      case 'share': return <Share2 size={48} color="#dc2626" />;
      case 'error': return <AlertCircle size={48} color="#ef4444" />;
      case 'info': return <Info size={48} color="#dc2626" />;
      default: return <Sparkles size={48} color="#dc2626" />;
    }
  };

  const getTitle = () => {
    switch(type) {
      case 'cart': return 'Added to Cart!';
      case 'wishlist': return 'Wishlist Updated';
      case 'share': return 'Share Product!';
      case 'error': return 'Oops!';
      case 'info': return 'Information';
      default: return 'Success!';
    }
  };

  return (
    <>
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease'
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '24px',
        zIndex: 2001,
        width: '90%',
        maxWidth: '340px',
        padding: '28px 20px',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.2s ease'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 16px',
          borderRadius: '50%',
          background: '#fff5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {getIcon()}
        </div>

        <h3 style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1a0a0a',
          marginBottom: '8px'
        }}>
          {getTitle()}
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#64748b',
          lineHeight: '1.5',
          marginBottom: productName ? '12px' : '0'
        }}>
          {message}
        </p>

        {productName && (
          <div style={{
            background: '#fff5f5',
            padding: '8px 12px',
            borderRadius: '12px',
            marginTop: '12px',
            border: '1px solid rgba(220,38,38,0.15)',
            display: 'inline-block'
          }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>
              {productName}
            </span>
          </div>
        )}

        <div style={{
          width: '60px',
          height: '3px',
          background: '#e2e8f0',
          borderRadius: '3px',
          margin: '20px auto 0',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: '#dc2626',
            borderRadius: '3px',
            animation: 'shrink 2s linear forwards'
          }} />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </>
  );
};

// Product Card Generator Component
const ProductCardGenerator = ({ product, currentPrice, onClose, onShare }) => {
  const canvasRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const generateCard = async () => {
    setIsGenerating(true);
    setGeneratedImage(null);
    setShowPreview(false);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size - optimized for social sharing
      const width = 800;
      const height = 500;
      canvas.width = width;
      canvas.height = height;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // --- Background ---
      // Main background gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#ffffff');
      gradient.addColorStop(0.5, '#fff8f8');
      gradient.addColorStop(1, '#fff0f0');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Decorative border
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 4;
      ctx.strokeRect(12, 12, width - 24, height - 24);

      // Corner decorations - cherry blossoms
      const drawCornerFlower = (x, y, size) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.font = `${size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#dc2626';
        ctx.globalAlpha = 0.15;
        ctx.fillText('🌸', 0, 0);
        ctx.globalAlpha = 1;
        ctx.restore();
      };
      
      drawCornerFlower(40, 40, 30);
      drawCornerFlower(width - 40, 40, 30);
      drawCornerFlower(40, height - 40, 30);
      drawCornerFlower(width - 40, height - 40, 30);

      // --- Top Brand Bar ---
      ctx.fillStyle = '#dc2626';
      ctx.fillRect(0, 0, width, 50);
      
      // Brand text
      ctx.fillStyle = 'white';
      ctx.font = 'bold 18px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('🌸 Sugandhi Flowers', 20, 25);
      
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.fillText('Premium Florist', width - 20, 25);

      // --- Product Image (Left side) ---
      const imgX = 30;
      const imgY = 70;
      const imgSize = 250;

      // Image placeholder with border
      ctx.shadowColor = 'rgba(0,0,0,0.1)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 4;

      // Image border
      ctx.strokeStyle = '#dc2626';
      ctx.lineWidth = 2;
      ctx.strokeRect(imgX, imgY, imgSize, imgSize);
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Load and draw product image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      try {
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => {
            // Use fallback image
            img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="250" height="250"%3E%3Crect width="250" height="250" fill="%23fff5f5"/%3E%3Ctext x="125" y="140" text-anchor="middle" fill="%23dc2626" font-size="80"%3E🌸%3C/text%3E%3C/svg%3E';
            setTimeout(resolve, 100);
          };
          
          // Use the product image or fallback
          const imageUrl = product?.image || 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80';
          img.src = imageUrl;
          
          // Set a timeout in case image loading hangs
          setTimeout(resolve, 5000);
        });
      } catch (err) {
        console.error('Image loading error:', err);
        // Continue with fallback
      }

      // Draw image with rounded corners
      const cornerRadius = 12;
      ctx.beginPath();
      ctx.moveTo(imgX + cornerRadius, imgY);
      ctx.arcTo(imgX + imgSize, imgY, imgX + imgSize, imgY + imgSize, cornerRadius);
      ctx.arcTo(imgX + imgSize, imgY + imgSize, imgX, imgY + imgSize, cornerRadius);
      ctx.arcTo(imgX, imgY + imgSize, imgX, imgY, cornerRadius);
      ctx.arcTo(imgX, imgY, imgX + imgSize, imgY, cornerRadius);
      ctx.closePath();
      ctx.clip();
      
      ctx.drawImage(img, imgX, imgY, imgSize, imgSize);

      // --- Product Details (Right side) ---
      const textX = imgX + imgSize + 30;
      let textY = 80;

      // Product Name
      ctx.fillStyle = '#1a0a0a';
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      
      const name = product?.name || 'Product';
      const maxNameLength = 25;
      const displayName = name.length > maxNameLength ? name.substring(0, maxNameLength) + '...' : name;
      
      // Wrap text if needed
      const lines = [];
      let currentLine = '';
      for (let char of displayName) {
        if (ctx.measureText(currentLine + char).width > 280) {
          lines.push(currentLine);
          currentLine = char;
        } else {
          currentLine += char;
        }
      }
      if (currentLine) lines.push(currentLine);

      lines.forEach((line, i) => {
        ctx.fillText(line, textX, textY + i * 32);
      });
      
      const nameHeight = lines.length * 32;
      textY += nameHeight + 15;

      // Discount badge if applicable
      const discountPercent = product?.oldPrice && product?.oldPrice > (currentPrice || 0) 
        ? Math.round(((product.oldPrice - (currentPrice || 0)) / product.oldPrice) * 100) 
        : 0;

      if (discountPercent > 0) {
        ctx.fillStyle = '#dc2626';
        ctx.font = 'bold 14px Arial';
        const discountText = `🔥 ${discountPercent}% OFF`;
        const discountWidth = ctx.measureText(discountText).width + 20;
        ctx.fillStyle = '#dc2626';
        ctx.roundRect(textX, textY - 5, discountWidth, 28, 14);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.textBaseline = 'middle';
        ctx.fillText(discountText, textX + 10, textY + 9);
        textY += 35;
      }

      // Price
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#dc2626';
      ctx.font = 'bold 32px Arial';
      ctx.fillText(`₹${(currentPrice || 0).toLocaleString()}`, textX, textY);
      textY += 42;

      // Old price if exists
      if (product?.oldPrice && product?.oldPrice > (currentPrice || 0)) {
        ctx.fillStyle = '#94a3b8';
        ctx.font = '18px Arial';
        ctx.textBaseline = 'top';
        ctx.fillText(`₹${product.oldPrice.toLocaleString()}`, textX, textY);
        
        // Strikethrough
        const oldPriceWidth = ctx.measureText(`₹${product.oldPrice.toLocaleString()}`).width;
        ctx.strokeStyle = '#94a3b8';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(textX, textY + 12);
        ctx.lineTo(textX + oldPriceWidth, textY + 12);
        ctx.stroke();
        textY += 30;
      }

      // Description (short)
      if (product?.description) {
        ctx.fillStyle = '#475569';
        ctx.font = '14px Arial';
        ctx.textBaseline = 'top';
        const desc = product.description;
        const maxDescLength = 60;
        const displayDesc = desc.length > maxDescLength ? desc.substring(0, maxDescLength) + '...' : desc;
        
        // Wrap description
        const descLines = [];
        let descCurrentLine = '';
        for (let char of displayDesc) {
          if (ctx.measureText(descCurrentLine + char).width > 280) {
            descLines.push(descCurrentLine);
            descCurrentLine = char;
          } else {
            descCurrentLine += char;
          }
        }
        if (descCurrentLine) descLines.push(descCurrentLine);
        
        descLines.forEach((line, i) => {
          ctx.fillText(line, textX, textY + i * 20);
        });
        textY += descLines.length * 20 + 10;
      }

      // --- Bottom Section ---
      // Shop Now Button
      const btnX = textX;
      const btnY = height - 85;
      const btnWidth = 150;
      const btnHeight = 40;

      // Button background
      ctx.shadowColor = 'rgba(220,38,38,0.3)';
      ctx.shadowBlur = 15;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 4;
      
      const btnGradient = ctx.createLinearGradient(btnX, btnY, btnX + btnWidth, btnY + btnHeight);
      btnGradient.addColorStop(0, '#dc2626');
      btnGradient.addColorStop(1, '#b91c1c');
      ctx.fillStyle = btnGradient;
      ctx.roundRect(btnX, btnY, btnWidth, btnHeight, 20);
      ctx.fill();
      
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      ctx.fillStyle = 'white';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(' Shop Now', btnX + btnWidth/2, btnY + btnHeight/2);

      // Website URL at bottom
      ctx.fillStyle = '#94a3b8';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      ctx.fillText('www.sugandhiflower.com • Fresh Flowers • Same Day Delivery', width - 30, height - 15);

      // Product tag badge (top right of image)
      if (product?.tag) {
        ctx.fillStyle = '#10b981';
        ctx.font = 'bold 12px Arial';
        const tagText = `✨ ${product.tag}`;
        const tagWidth = ctx.measureText(tagText).width + 20;
        ctx.roundRect(imgX + imgSize - tagWidth - 10, imgY + 10, tagWidth, 28, 14);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tagText, imgX + imgSize - tagWidth/2 - 10, imgY + 24);
      }

      // --- Convert to image ---
      const dataUrl = canvas.toDataURL('image/png');
      setGeneratedImage(dataUrl);
      setShowPreview(true);
      
    } catch (error) {
      console.error('Card generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.download = `${(product?.name || 'product').replace(/\s+/g, '_')}_card.png`;
    link.href = generatedImage;
    link.click();
  };

  const handleShareImage = async () => {
    if (!generatedImage) return;
    
    try {
      // Convert base64 to blob
      const response = await fetch(generatedImage);
      const blob = await response.blob();
      const file = new File([blob], 'product_card.png', { type: 'image/png' });

      if (navigator.share) {
        await navigator.share({
          title: `${product?.name} - Sugandhi Flowers`,
          text: `🌸 Check out ${product?.name} from Sugandhi Flowers!`,
          files: [file]
        });
      } else {
        // Fallback: download the image
        handleDownload();
      }
      onClose();
    } catch (error) {
      if (error.name !== 'AbortError') {
        handleDownload();
      }
    }
  };

  const handleWhatsAppShare = () => {
    if (!generatedImage) return;
    
    // For WhatsApp, we share the image by downloading it first
    // The user can then attach it in WhatsApp
    handleDownload();
    // Also show a message with the link
    const message = ` ${product?.name}\n Price: ₹${(currentPrice || 0).toLocaleString()}\n\n Sugandhi Flowers - Premium Florist\n${window.location.href}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      zIndex: 2600,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '24px',
        maxWidth: '850px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#1a0a0a', margin: 0 }}>
              <ImageIcon size={20} style={{ display: 'inline', marginRight: '8px', color: '#dc2626' }} />
              Product Card
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
              Generate a beautiful product card to share on social media
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: '#f1f5f9',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Hidden Canvas */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Controls */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={generateCard}
            disabled={isGenerating}
            style={{
              padding: '10px 24px',
              background: isGenerating ? '#94a3b8' : '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isGenerating ? (
              <><div className="spinner" style={{ width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} /> Generating...</>
            ) : (
              <><Camera size={18} /> Generate Card</>
            )}
          </button>

          {generatedImage && (
            <>
              <button
                onClick={handleDownload}
                style={{
                  padding: '10px 20px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Download size={18} /> Download
              </button>
              <button
                onClick={handleWhatsAppShare}
                style={{
                  padding: '10px 20px',
                  background: '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <MessageCircle size={18} /> WhatsApp
              </button>
              <button
                onClick={handleShareImage}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Share size={18} /> Share
              </button>
            </>
          )}
        </div>

        {/* Preview */}
        {showPreview && generatedImage ? (
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center',
            border: '1px solid #e2e8f0'
          }}>
            <img 
              src={generatedImage} 
              alt="Product Card"
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            />
            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
              Right-click to save or use the buttons below
            </p>
          </div>
        ) : (
          <div style={{
            background: '#f8fafc',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            border: '2px dashed #e2e8f0'
          }}>
            <ImageIcon size={48} color="#cbd5e1" />
            <p style={{ color: '#94a3b8', marginTop: '12px' }}>
              Click "Generate Card" to create a beautiful product card
            </p>
            <p style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>
              The card will include product image, name, price, and description
            </p>
          </div>
        )}
      </div>

      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        canvas {
          display: none;
        }
      `}</style>
    </div>
  );
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, product, currentPrice, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCardGenerator, setShowCardGenerator] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isOpen) return null;

  const productImage = product?.image || '';
  const productName = product?.name || '';
  const productPrice = currentPrice || 0;
  const productDescription = product?.description || '';

  // Generate share text
  const generateShareText = () => {
    return `🌸 ${productName}\n💰 Price: ₹${productPrice.toLocaleString()}\n${productDescription ? `📝 ${productDescription.substring(0, 80)}...\n` : ''}\n✨ Sugandhi Flowers - Premium Florist\n\n${shareUrl}`;
  };

  const handleWhatsAppShare = () => {
    const message = generateShareText();
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  const handleTelegramShare = () => {
    const message = generateShareText();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`;
    window.open(telegramUrl, '_blank');
    onClose();
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${productName} - Sugandhi Flowers`,
          text: `🌸 ${productName} | ₹${productPrice.toLocaleString()} | Sugandhi Flowers`,
          url: shareUrl,
        });
        onClose();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
          handleWhatsAppShare();
        }
      }
    } else {
      handleWhatsAppShare();
    }
  };

  const handleCopyLink = async () => {
    const fullText = generateShareText();
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = fullText;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const shareOptions = [
    { 
      id: 'whatsapp', 
      icon: <MessageCircle size={24} />, 
      label: 'WhatsApp',
      color: '#25D366',
      onClick: handleWhatsAppShare,
      show: true
    },
    { 
      id: 'native', 
      icon: <Share size={24} />, 
      label: 'Share via...',
      color: '#dc2626',
      onClick: handleNativeShare,
      show: true
    },
    { 
      id: 'telegram', 
      icon: <Send size={24} />, 
      label: 'Telegram',
      color: '#0088cc',
      onClick: handleTelegramShare,
      show: true
    },
    { 
      id: 'copy', 
      icon: <Copy size={24} />, 
      label: copied ? 'Copied!' : 'Copy Link',
      color: '#64748b',
      onClick: handleCopyLink,
      show: true
    }
  ];

  return (
    <>
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 2500,
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease'
        }}
      />
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'white',
        borderRadius: '24px',
        zIndex: 2501,
        width: isMobile ? '92%' : '420px',
        maxWidth: '420px',
        padding: '24px',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.2s ease',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#fff5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <Share2 size={28} color="#dc2626" />
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: '#1a0a0a' }}>
            Share Product
          </h3>
          <p style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
            Share this beautiful product with your friends
          </p>
        </div>

        {/* Product Preview */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '16px',
          border: '1px solid rgba(220,38,38,0.1)'
        }}>
          <img 
            src={productImage} 
            alt={productName}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '8px',
              objectFit: 'cover',
              border: '1px solid #e2e8f0'
            }}
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=100&q=80';
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1a0a0a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {productName}
            </div>
            <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '15px' }}>
              ₹{productPrice.toLocaleString()}
            </div>
            {productDescription && (
              <div style={{ fontSize: '11px', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {productDescription.substring(0, 50)}...
              </div>
            )}
          </div>
        </div>

        {/* Generate Product Card Button - HIGHLIGHTED */}
        <button
          onClick={() => {
            onClose();
            setTimeout(() => setShowCardGenerator(true), 300);
          }}
          style={{
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '16px',
            boxShadow: '0 4px 15px rgba(220,38,38,0.3)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(220,38,38,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(220,38,38,0.3)';
          }}
        >
          <ImageIcon size={20} /> Generate Product Card
        </button>

        {/* Share Options */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', 
          gap: '10px',
          marginBottom: '12px'
        }}>
          {shareOptions.map(option => (
            <button
              key={option.id}
              onClick={option.onClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                padding: '12px 8px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                width: '100%'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = option.color;
                e.currentTarget.style.background = `${option.color}10`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ 
                color: option.color,
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: `${option.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {option.icon}
              </div>
              <span style={{ 
                fontSize: isMobile ? '10px' : '11px', 
                fontWeight: 500, 
                color: copied && option.id === 'copy' ? '#10b981' : '#64748b',
                textAlign: 'center'
              }}>
                {option.label}
              </span>
            </button>
          ))}
        </div>

        {/* URL Display */}
        <div style={{
          background: '#f1f5f9',
          borderRadius: '8px',
          padding: '8px 12px',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{ 
            fontSize: '11px', 
            color: '#64748b', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {shareUrl}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '10px',
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '10px',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            color: '#64748b',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
        >
          Close
        </button>
      </div>

      {/* Product Card Generator Modal */}
      {showCardGenerator && (
        <ProductCardGenerator
          product={product}
          currentPrice={currentPrice}
          onClose={() => setShowCardGenerator(false)}
          onShare={() => {}}
        />
      )}
    </>
  );
};

// Full-Screen Image Viewer Component (kept from previous version)
const FullScreenImageViewer = ({ images, initialIndex, onClose, onVariantSelect, product }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const minSwipeDistance = 50;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const goNext = () => {
    if (currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const onTouchStartHandler = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMoveHandler = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEndHandler = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) goNext();
      else goPrev();
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentImage = images[currentIndex];
  
  const getVariantForImage = (img) => {
    if (img.type === 'variant' && img.variant) {
      return img.variant;
    }
    return null;
  };

  const handleVariantSelect = () => {
    const variant = getVariantForImage(currentImage);
    if (variant && onVariantSelect) {
      onVariantSelect(variant);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.95)',
        zIndex: 3000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none'
      }}
      onTouchStart={onTouchStartHandler}
      onTouchMove={onTouchMoveHandler}
      onTouchEnd={onTouchEndHandler}
    >
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          zIndex: 3002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      >
        <X size={24} />
      </button>

      <div style={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        color: 'white',
        fontSize: isMobile ? '14px' : '18px',
        fontWeight: 600,
        zIndex: 3002,
        textAlign: 'center',
        maxWidth: '80%',
        textShadow: '0 2px 8px rgba(0,0,0,0.5)',
        pointerEvents: 'none'
      }}>
        {product?.name || 'Product View'}
      </div>

      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '60px 10px 80px' : '70px 20px 100px'
      }}>
        <img
          src={currentImage?.url}
          alt={`Slide ${currentIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        />

        {images.length > 1 && !isMobile && (
          <>
            {currentIndex > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                style={{
                  position: 'absolute',
                  left: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <ChevronLeft size={28} />
              </button>
            )}
            {currentIndex < images.length - 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <ChevronRight size={28} />
              </button>
            )}
          </>
        )}

        {images.length > 1 && (
          <div style={{
            position: 'absolute',
            bottom: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '4px 14px',
            borderRadius: 20,
            fontSize: isMobile ? '12px' : '14px',
            backdropFilter: 'blur(10px)',
            zIndex: 3002
          }}>
            {currentIndex + 1} / {images.length}
          </div>
        )}

        {getVariantForImage(currentImage) && (
          <div style={{
            position: 'absolute',
            bottom: 56,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(220,38,38,0.9)',
            color: 'white',
            padding: '6px 16px',
            borderRadius: 20,
            fontSize: isMobile ? '11px' : '13px',
            fontWeight: 500,
            zIndex: 3002,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backdropFilter: 'blur(10px)',
            cursor: 'pointer',
            border: 'none'
          }}
          onClick={handleVariantSelect}
          >
            <Check size={14} />
            {getVariantForImage(currentImage)?.color && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '50%', 
                  background: getVariantForImage(currentImage).color.toLowerCase() 
                }} />
                {getVariantForImage(currentImage).color}
              </span>
            )}
            {getVariantForImage(currentImage)?.size && (
              <span>• {getVariantForImage(currentImage).size}</span>
            )}
            <span>• ₹{getVariantForImage(currentImage).price}</span>
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: 60,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: isMobile ? '8px' : '12px',
          padding: '0 16px',
          overflowX: 'auto',
          zIndex: 3002,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}
        className="hide-scrollbar"
        >
          {images.map((img, idx) => {
            const isActive = idx === currentIndex;
            const isVariant = img.type === 'variant';
            return (
              <div
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                style={{
                  width: isMobile ? '44px' : '60px',
                  height: isMobile ? '44px' : '60px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: isActive ? '3px solid #dc2626' : '2px solid rgba(255,255,255,0.3)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                }}
              >
                <img
                  src={img.url}
                  alt=""
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                {isVariant && (
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(220,38,38,0.8)',
                    padding: '1px 4px',
                    fontSize: '6px',
                    color: 'white',
                    textAlign: 'center',
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}>
                    {img.variant?.color || 'Variant'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isMobile && images.length > 1 && (
        <div style={{
          position: 'absolute',
          bottom: 130,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.4)',
          fontSize: '11px',
          zIndex: 3002,
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          ← Swipe to navigate →
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

// Main ProductDetailPage Component
const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { products: allProducts, loading } = useProducts();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedTab, setSelectedTab] = useState('description');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(true);
  
  // Full-screen viewer state
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [fullScreenIndex, setFullScreenIndex] = useState(0);
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  
  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    message: '',
    type: 'cart',
    productName: null
  });

  const showDialog = (message, type = 'cart', productName = null) => {
    setDialog({ isOpen: true, message, type, productName });
  };

  const closeDialog = () => {
    setDialog({ isOpen: false, message: '', type: 'cart', productName: null });
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!loading && allProducts && allProducts.length > 0) {
      let found = allProducts.find(p => p.id === productId);
      if (!found) {
        found = allProducts.find(p => p.firebaseKey === productId);
      }
      setProduct(found);
      if (found) {
        setSelectedImageIndex(0);
        setSelectedVariant(null);
        setQuantity(1);
        const related = allProducts.filter(p => 
          p.id !== found.id && (p.category === found.category || p.tag === found.tag)
        ).slice(0, 8);
        setRelatedProducts(related);
      }
      setLoadingProduct(false);
    } else if (!loading && allProducts && allProducts.length === 0) {
      setLoadingProduct(false);
    }
  }, [allProducts, loading, productId]);

  const handleGoBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity < 1) return;
    if (newQuantity > 10) {
      showDialog('Maximum 10 items allowed', 'error');
      return;
    }
    setQuantity(newQuantity);
  };

  const discountPercent = product?.oldPrice && product.oldPrice > (selectedVariant?.price || product?.price) 
    ? Math.round(((product.oldPrice - (selectedVariant?.price || product?.price)) / product.oldPrice) * 100) : 0;
  
  const currentPrice = selectedVariant ? selectedVariant.price : product?.price;
  const finalPrice = currentPrice * quantity;

  // Collect all images with variant info
  const allImages = [
    { type: 'main', url: product?.image, variant: null },
    ...(product?.variants?.flatMap(variant => 
      (variant.images || []).map(img => ({ 
        type: 'variant', 
        url: img.url || img.preview, 
        variant: variant
      }))
    ) || [])
  ].filter(img => img.url);

  const currentImage = allImages[selectedImageIndex]?.url || product?.image;

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
    const img = allImages[index];
    if (img.type === 'variant' && img.variant) {
      setSelectedVariant(img.variant);
    }
  };

  // Open full-screen viewer
  const openFullScreen = (index) => {
    setFullScreenIndex(index);
    setShowFullScreen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close full-screen viewer
  const closeFullScreen = () => {
    setShowFullScreen(false);
    document.body.style.overflow = 'auto';
  };

  // Handle variant selection from full-screen viewer
  const handleVariantSelectFromFullScreen = (variant) => {
    setSelectedVariant(variant);
    const variantImageIndex = allImages.findIndex(img => img.variant === variant);
    if (variantImageIndex !== -1) {
      setSelectedImageIndex(variantImageIndex);
    }
  };

  // Share handler
  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleAddToCart = () => {
    if (!user) { 
      showDialog('Please login to continue', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return; 
    }
    
    const cartItem = {
      id: product.id,
      name: product.name,
      price: currentPrice,
      image: currentImage,
      quantity: quantity,
      variantId: selectedVariant ? selectedVariant.id : 'standard',
      variantName: selectedVariant ? `${selectedVariant.color} ${selectedVariant.size}`.trim() : 'Standard',
      variantColor: selectedVariant?.color || null,
      variantSize: selectedVariant?.size || null,
      cartKey: `${product.id}_${selectedVariant?.id || 'standard'}`
    };
    
    addToCart(cartItem);
    showDialog(`${quantity}x item added to your cart!`, 'cart', product.name);
  };

  const handleBuyNow = () => {
    if (!user) { 
      showDialog('Please login to continue', 'info');
      setTimeout(() => navigate('/login'), 2000);
      return; 
    }
    
    const orderData = {
      items: [{
        id: product.id,
        name: product.name,
        price: currentPrice,
        quantity: quantity,
        image: currentImage,
        variantId: selectedVariant ? selectedVariant.id : 'standard',
        variantName: selectedVariant ? `${selectedVariant.color} ${selectedVariant.size}`.trim() : 'Standard'
      }],
      totalAmount: finalPrice,
      paymentMethod: 'Online Payment'
    };
    sessionStorage.setItem('buyNowProduct', JSON.stringify(orderData));
    navigate('/checkout');
  };

  if (loadingProduct || loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f3f6', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #dc2626', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }
  
  if (!product) {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f3f6', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🌸</div>
        <h2 style={{ fontSize: '1.5rem', color: '#212121', marginBottom: 8 }}>Product Not Found</h2>
        <p style={{ color: '#878787', marginBottom: 24, textAlign: 'center' }}>The product you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate('/')} style={{ padding: '12px 28px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Continue Shopping</button>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f3f6' }}>
      {/* Custom Auto-close Dialog */}
      <CustomDialog 
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        message={dialog.message}
        type={dialog.type}
        productName={dialog.productName}
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        product={product}
        currentPrice={currentPrice}
        shareUrl={shareUrl}
      />

      {/* Full-Screen Image Viewer */}
      {showFullScreen && (
        <FullScreenImageViewer
          images={allImages}
          initialIndex={fullScreenIndex}
          onClose={closeFullScreen}
          onVariantSelect={handleVariantSelectFromFullScreen}
          product={product}
        />
      )}

      {/* Cherry Theme Sticky Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#dc2626',
        padding: isMobile ? '8px 12px' : '10px 20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button 
            onClick={handleGoBack} 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              padding: isMobile ? '6px 12px' : '8px 16px',
              borderRadius: 4,
              cursor: 'pointer',
              color: 'white',
              fontWeight: 500,
              fontSize: isMobile ? '12px' : '13px'
            }}
          >
            <ArrowLeft size={isMobile ? 14 : 16} /> Back
          </button>
          
          <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px' }}>
            <button 
              onClick={() => {
                setIsWishlisted(!isWishlisted);
                showDialog(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist', 'wishlist');
              }}
              style={{
                padding: isMobile ? '6px 10px' : '8px 14px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'white',
                border: 'none'
              }}
            >
              <Heart size={isMobile ? 14 : 16} fill={isWishlisted ? 'white' : 'none'} />
            </button>
            <button 
              onClick={handleShare}
              style={{
                padding: isMobile ? '6px 10px' : '8px 14px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'white',
                border: 'none'
              }}
            >
              <Share2 size={isMobile ? 14 : 16} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '10px' : '20px' }}>
        {/* Main Product Section */}
        <div style={{ 
          background: 'white', 
          borderRadius: isMobile ? 8 : 12, 
          padding: isMobile ? '10px' : '20px',
          marginBottom: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '12px' : '30px' }}>
            
            {/* Left: Image Gallery */}
            <div style={{ flex: isMobile ? 'auto' : 1 }}>
              {/* Main Image with Full-Screen Click */}
              <div 
                onClick={() => openFullScreen(selectedImageIndex)}
                style={{ 
                  position: 'relative', 
                  background: '#fff',
                  borderRadius: 8,
                  textAlign: 'center',
                  border: '1px solid #f0f0f0',
                  marginBottom: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer'
                }}
              >
                <img 
                  src={currentImage} 
                  alt={product.name} 
                  style={{ 
                    width: '100%', 
                    maxHeight: isMobile ? '220px' : '400px', 
                    objectFit: 'contain',
                    transition: 'transform 0.2s'
                  }}
                />
                {discountPercent > 0 && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '8px', 
                    left: '8px', 
                    background: '#dc2626', 
                    color: 'white', 
                    padding: `${isMobile ? '2px 6px' : '4px 10px'}`, 
                    borderRadius: 2, 
                    fontSize: isMobile ? '10px' : '12px', 
                    fontWeight: 700 
                  }}>
                    {discountPercent}% OFF
                  </div>
                )}
                {/* Full-screen indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  right: '8px',
                  background: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Maximize2 size={12} /> Tap to expand
                </div>
              </div>

              {/* Thumbnails */}
              {allImages.length > 1 && (
                <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', padding: '4px 0', justifyContent: 'center' }}>
                  {allImages.map((img, idx) => {
                    const isVariant = img.type === 'variant';
                    return (
                      <div 
                        key={idx} 
                        onClick={() => handleImageClick(idx)}
                        style={{ 
                          width: isMobile ? '45px' : '60px', 
                          height: isMobile ? '45px' : '60px', 
                          borderRadius: 4, 
                          overflow: 'hidden', 
                          border: selectedImageIndex === idx ? '2px solid #dc2626' : '1px solid #e2e8f0', 
                          cursor: 'pointer', 
                          flexShrink: 0,
                          background: '#f8fafc',
                          position: 'relative'
                        }}
                      >
                        <img 
                          src={img.url} 
                          alt="thumbnail" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        />
                        {isVariant && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background: 'rgba(220,38,38,0.7)',
                            padding: '1px 4px',
                            fontSize: '6px',
                            color: 'white',
                            textAlign: 'center',
                            fontWeight: 600
                          }}>
                            {img.variant?.color || 'V'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: Product Info */}
            <div style={{ flex: 1.5 }}>
              {/* Product Title */}
              <h1 style={{ 
                fontSize: isMobile ? '16px' : '22px', 
                fontWeight: 500, 
                color: '#212121', 
                marginBottom: '6px',
                lineHeight: '1.3'
              }}>
                {product.name}
              </h1>

              {/* Price Section */}
              <div style={{ marginBottom: '12px', background: '#fff5f5', padding: isMobile ? '8px' : '12px', borderRadius: 8 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: isMobile ? '20px' : '26px', fontWeight: 700, color: '#dc2626' }}>
                    ₹{currentPrice?.toLocaleString()}
                  </span>
                  {product.oldPrice && (
                    <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#878787', textDecoration: 'line-through' }}>
                      ₹{product.oldPrice?.toLocaleString()}
                    </span>
                  )}
                  {discountPercent > 0 && (
                    <span style={{ 
                      background: '#fff5f5', 
                      color: '#dc2626', 
                      padding: '2px 6px', 
                      borderRadius: 2, 
                      fontSize: '11px', 
                      fontWeight: 600 
                    }}>
                      Save {discountPercent}%
                    </span>
                  )}
                </div>
              </div>

              {/* Variants */}
              {product.variants && product.variants.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '12px', fontWeight: 500, marginBottom: '8px', color: '#212121' }}>
                    Select Variant:
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {product.variants.map((variant, idx) => {
                      const isSelected = selectedVariant === variant;
                      return (
                        <div 
                          key={idx} 
                          onClick={() => { 
                            setSelectedVariant(variant); 
                            const variantImgIndex = allImages.findIndex(img => img.variant === variant);
                            if (variantImgIndex !== -1) setSelectedImageIndex(variantImgIndex);
                          }}
                          style={{
                            background: isSelected ? '#fff5f5' : 'white',
                            border: isSelected ? '1px solid #dc2626' : '1px solid #e2e8f0',
                            borderRadius: 6,
                            padding: isMobile ? '6px 10px' : '8px 12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minWidth: '80px'
                          }}
                        >
                          {variant.color && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '3px' }}>
                              <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: variant.color.toLowerCase() }} />
                              <span style={{ fontWeight: 500, fontSize: '12px' }}>{variant.color}</span>
                            </div>
                          )}
                          {variant.size && (
                            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px' }}>{variant.size}</div>
                          )}
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#dc2626' }}>₹{variant.price}</div>
                          {isSelected && <Check size={12} color="#dc2626" style={{ marginTop: '4px' }} />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 500, color: '#212121' }}>Quantity:</span>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}>
                    <button
                      onClick={() => handleQuantityChange(quantity - 1)}
                      style={{ 
                        padding: isMobile ? '6px 10px' : '8px 14px', 
                        background: '#f8fafc', 
                        border: 'none', 
                        cursor: 'pointer', 
                        fontSize: '14px', 
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                    >
                      <Minus size={isMobile ? 12 : 14} />
                    </button>
                    <span style={{ padding: '0 12px', fontSize: '14px', fontWeight: 500, minWidth: '40px', textAlign: 'center' }}>
                      {quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      style={{ 
                        padding: isMobile ? '6px 10px' : '8px 14px', 
                        background: '#f8fafc', 
                        border: 'none', 
                        cursor: 'pointer', 
                        fontSize: '14px', 
                        fontWeight: 600,
                        transition: 'all 0.2s'
                      }}
                    >
                      <Plus size={isMobile ? 12 : 14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                <button 
                  onClick={handleAddToCart}
                  style={{ 
                    flex: 1, 
                    padding: isMobile ? '10px' : '12px', 
                    background: '#ff9f00', 
                    color: '#212121', 
                    border: 'none', 
                    borderRadius: 4, 
                    fontSize: isMobile ? '13px' : '14px', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <ShoppingCart size={isMobile ? 16 : 18} /> Add to Cart • ₹{finalPrice.toLocaleString()}
                </button>
                <button 
                  onClick={handleBuyNow}
                  style={{ 
                    flex: 1, 
                    padding: isMobile ? '10px' : '12px', 
                    background: '#fb641b', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: 4, 
                    fontSize: isMobile ? '13px' : '14px', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Zap size={isMobile ? 16 : 18} /> Buy Now
                </button>
              </div>

              {/* Tag and Origin */}
              <div style={{ marginTop: '10px' }}>
                {product.tag && (
                  <span style={{ 
                    background: '#fff5f5', 
                    color: '#dc2626', 
                    padding: '3px 8px', 
                    borderRadius: 2, 
                    fontSize: '10px', 
                    fontWeight: 500,
                    display: 'inline-block',
                    marginRight: '6px',
                    marginBottom: '6px'
                  }}>
                    🏷️ {product.tag}
                  </span>
                )}
                {product.origin && (
                  <span style={{ fontSize: '11px', color: '#878787', display: 'inline-block' }}>
                    🌍 Origin: {product.origin}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Description & Details Tabs */}
        <div style={{ 
          background: 'white', 
          borderRadius: isMobile ? 8 : 12, 
          padding: isMobile ? '10px' : '20px',
          marginBottom: '12px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? '16px' : '24px', 
            borderBottom: '1px solid #e2e8f0'
          }}>
            <button
              onClick={() => setSelectedTab('description')}
              style={{
                padding: isMobile ? '8px 0' : '10px 0',
                background: 'none',
                border: 'none',
                borderBottom: selectedTab === 'description' ? '2px solid #dc2626' : '2px solid transparent',
                color: selectedTab === 'description' ? '#dc2626' : '#64748b',
                fontWeight: selectedTab === 'description' ? 600 : 400,
                fontSize: isMobile ? '12px' : '13px',
                cursor: 'pointer'
              }}
            >
              Description
            </button>
            <button
              onClick={() => setSelectedTab('details')}
              style={{
                padding: isMobile ? '8px 0' : '10px 0',
                background: 'none',
                border: 'none',
                borderBottom: selectedTab === 'details' ? '2px solid #dc2626' : '2px solid transparent',
                color: selectedTab === 'details' ? '#dc2626' : '#64748b',
                fontWeight: selectedTab === 'details' ? 600 : 400,
                fontSize: isMobile ? '12px' : '13px',
                cursor: 'pointer'
              }}
            >
              Product Details
            </button>
          </div>

          <div style={{ paddingTop: '12px' }}>
            {selectedTab === 'description' && (
              <p style={{ lineHeight: 1.5, color: '#475569', fontSize: isMobile ? '13px' : '14px' }}>
                {product.description || 'No description available for this product.'}
              </p>
            )}

            {selectedTab === 'details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ color: '#64748b', fontSize: '12px' }}>Product Name</span>
                  <span style={{ fontWeight: 500, fontSize: '12px' }}>{product.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ color: '#64748b', fontSize: '12px' }}>Price</span>
                  <span style={{ fontWeight: 700, color: '#dc2626', fontSize: '12px' }}>₹{currentPrice?.toLocaleString()}</span>
                </div>
                {product.origin && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ color: '#64748b', fontSize: '12px' }}>Origin</span>
                    <span style={{ fontSize: '12px' }}>{product.origin}</span>
                  </div>
                )}
                {product.tag && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                    <span style={{ color: '#64748b', fontSize: '12px' }}>Tag</span>
                    <span style={{ background: '#fff5f5', color: '#dc2626', padding: '2px 8px', borderRadius: 2, fontSize: '11px' }}>
                      {product.tag}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div style={{ 
            background: 'white', 
            borderRadius: isMobile ? 8 : 12, 
            padding: isMobile ? '10px' : '20px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <h2 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600, marginBottom: '12px', color: '#212121' }}>
              You may also like
            </h2>
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              overflowX: 'auto', 
              paddingBottom: '6px',
              scrollbarWidth: 'thin'
            }}
            className="hide-scrollbar"
            >
              {relatedProducts.map(rel => {
                const relDiscount = rel.oldPrice && rel.oldPrice > rel.price
                  ? Math.round(((rel.oldPrice - rel.price) / rel.oldPrice) * 100) : 0;
                return (
                  <div 
                    key={rel.id} 
                    onClick={() => navigate(`/product/${rel.id}`)}
                    style={{ 
                      flex: '0 0 auto', 
                      width: isMobile ? '110px' : '130px', 
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                  >
                    <div style={{ 
                      background: '#f8fafc', 
                      borderRadius: 8, 
                      overflow: 'hidden',
                      aspectRatio: '1/1',
                      position: 'relative'
                    }}>
                      <img 
                        src={rel.image} 
                        alt={rel.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'}
                      />
                      {relDiscount > 0 && (
                        <div style={{ position: 'absolute', bottom: '4px', left: '4px', background: '#dc2626', color: 'white', padding: '2px 5px', borderRadius: 2, fontSize: '9px', fontWeight: 700 }}>
                          {relDiscount}% OFF
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '6px 2px' }}>
                      <h3 style={{ fontSize: '11px', fontWeight: 500, marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rel.name}
                      </h3>
                      <div style={{ fontWeight: 700, color: '#dc2626', fontSize: '12px' }}>₹{rel.price?.toLocaleString()}</div>
                      {rel.oldPrice && (
                        <div style={{ fontSize: '9px', color: '#878787', textDecoration: 'line-through' }}>₹{rel.oldPrice?.toLocaleString()}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          height: 3px;
        }
        .hide-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .hide-scrollbar::-webkit-scrollbar-thumb {
          background: #dc2626;
          border-radius: 3px;
        }
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;